import { first, get, isNumber, isObject } from 'lodash';
import classNames from 'classnames';

import { formatPropertyValue } from '@/explore/utils/format';
import { Icon } from '@/components/icon';
import { ListRecord } from '@/explore/grouping';
import { Field, TimeAggregationPeriod } from '@/explore/types';
import { Skeleton } from '@/components/skeleton-loader';
import { getTimeAggregationPeriodLabel } from '@/explore/pipeline/format';

import styles from './big-number.module.scss';

/**
 * Calculate the default offset for comparison based on the comparison context
 * @param field - The field being compared
 * @param comparisonField - The field to compare against
 * @param comparisonMode - The comparison mode ('value' or 'period_to_date')
 * @returns The default offset to use for comparison
 */
export const getDefaultOffset = (
  field: Field,
  comparisonField?: Field,
  comparisonMode?: string,
): number => {
  if (comparisonMode === 'period_to_date') {
    return 1; // Previous period for period-to-date comparisons
  }

  // For regular comparisons
  if (comparisonField && field.key === comparisonField.key) {
    return 1; // Same field = compare with previous row (time series)
  }
  return 0; // Different field = compare with same row (cross-field)
};

interface BigNumberProps {
  field: Field;
  comparisonField?: Field;
  data: ListRecord[];
  comparisonType?: 'absolute' | 'percentage';
  direction?: 'higher' | 'lower';
  showLabel?: boolean;
  hasHeader?: boolean;
  isTimeSeries?: boolean;
  loading?: boolean;
  mainAxisField?: Field;
  // Period-to-date specific props
  comparisonMode?: 'value' | 'period_to_date';
  periodToDateData?: ListRecord[];
  isPeriodToDateLoading?: boolean;
  isPeriodToDateError?: boolean;
}

export interface GetDeltaParams {
  field: Field;
  comparisonField?: Field;
  data: ListRecord[];
  comparisonType?: 'absolute' | 'percentage';
  comparisonMode?: 'value' | 'period_to_date';
  periodToDateData?: ListRecord[];
}

export interface GetDeltaLabelParams extends BigNumberProps {
  deltaValue?: number;
}

const getComparisonLabel = (
  precision: TimeAggregationPeriod | undefined,
  comparisonField: Field | undefined,
  actualField: Field | undefined,
  comparisonMode: 'value' | 'period_to_date' | undefined,
): string | undefined => {
  if (precision === undefined) {
    return undefined;
  }

  const periodLabel = getTimeAggregationPeriodLabel(precision).toLowerCase();

  if (comparisonMode === 'period_to_date') {
    const timeInfo = `previous ${periodLabel} to date`;
    const isSameField = actualField?.key === comparisonField?.key;
    if (isSameField || comparisonField?.name === undefined) {
      return `vs ${timeInfo}`;
    }
    return `vs ${comparisonField.name} ${timeInfo}`;
  }

  return `vs last ${periodLabel}`;
};

export const getDeltaLabel = ({
  field,
  comparisonField,
  data,
  showLabel,
  deltaValue,
  mainAxisField,
  comparisonMode,
  periodToDateData,
  isPeriodToDateError,
}: GetDeltaLabelParams) => {
  if (!comparisonField || showLabel !== true) {
    return undefined;
  }

  // Handle period-to-date specific cases
  if (comparisonMode === 'period_to_date') {
    if (isPeriodToDateError === true) {
      return 'Failed to fetch comparison';
    }
    if (!periodToDateData || periodToDateData.length === 0 || deltaValue === undefined) {
      return 'No data to compare';
    }
    return getComparisonLabel(mainAxisField?.precision, comparisonField, field, comparisonMode);
  }

  // Regular comparison logic
  if ((data.length < 2 && field.key === comparisonField.key) || deltaValue === undefined) {
    return 'No comparison available';
  }

  // Time series comparison (same field)
  if (comparisonField.key === field.key) {
    return (
      getComparisonLabel(comparisonField?.precision, comparisonField, field, comparisonMode) ??
      `vs ${comparisonField.name}`
    );
  }

  // Cross-field comparison
  return `vs ${comparisonField.name}`;
};

const getComparisonValue = ({
  field,
  comparisonField,
  data,
  comparisonMode,
  periodToDateData,
}: {
  field: Field;
  comparisonField: Field;
  data: ListRecord[];
  comparisonMode?: 'value' | 'period_to_date';
  periodToDateData?: ListRecord[];
}) => {
  const offset = getDefaultOffset(field, comparisonField, comparisonMode);

  if (comparisonMode === 'period_to_date' && periodToDateData) {
    if (periodToDateData.length <= offset) {
      return undefined;
    }
    return get(periodToDateData.at(offset), comparisonField.key, 0);
  }

  if (field.key === comparisonField.key && data.length <= offset) {
    return undefined;
  }
  return get(data.at(offset), comparisonField.key, 0);
};

export const getDelta = ({
  field,
  comparisonField,
  data,
  comparisonType,
  comparisonMode,
  periodToDateData,
}: GetDeltaParams) => {
  if (comparisonField === undefined || data.length === 0) {
    return undefined;
  }

  const displayPercentage = comparisonType === 'percentage';
  const currentValue = get(first(data), field.key, 0);
  const comparisonValue = getComparisonValue({
    field,
    comparisonField,
    data,
    comparisonMode,
    periodToDateData,
  });

  if (
    !isNumber(currentValue) ||
    !isNumber(comparisonValue) ||
    (displayPercentage && comparisonValue === 0)
  ) {
    return undefined;
  }

  return displayPercentage
    ? (currentValue - comparisonValue) / comparisonValue
    : currentValue - comparisonValue;
};

type BigNumberValueProps = {
  data: ListRecord[];
  field: Field;
  loading: boolean;
};

const BigNumberValue = ({ data, field, loading }: BigNumberValueProps) => {
  const value = get(first(data), field.key, 'N/A');
  if (loading) {
    return (
      <div className={styles.bigNumberLoader}>
        <Skeleton delay={50} light />
      </div>
    );
  }

  return isObject(value) ? (
    <pre className={styles.bigNumberValue}>{JSON.stringify(value, null, 2)}</pre>
  ) : (
    <div className={styles.bigNumberValue}>
      {formatPropertyValue(value, { field, style: 'compactLong' })}
    </div>
  );
};

type BigNumberDeltaProps = {
  field: Field;
  comparisonField?: Field;
  data: ListRecord[];
  comparisonType?: 'absolute' | 'percentage';
  direction?: 'higher' | 'lower';
  showLabel?: boolean;
  loading?: boolean;
  mainAxisField?: Field;
  comparisonMode?: 'value' | 'period_to_date';
  periodToDateData?: ListRecord[];
  periodToDateLoading?: boolean;
  isPeriodToDateError?: boolean;
};

const BigNumberDelta = ({
  field,
  comparisonField,
  data,
  comparisonType,
  showLabel,
  direction,
  loading = false,
  mainAxisField,
  comparisonMode,
  periodToDateData,
  periodToDateLoading,
  isPeriodToDateError,
}: BigNumberDeltaProps) => {
  const deltaValue = getDelta({
    field,
    comparisonField,
    data,
    comparisonType,
    comparisonMode,
    periodToDateData,
  });
  const deltaLabel = getDeltaLabel({
    field,
    comparisonField,
    data,
    showLabel,
    deltaValue,
    mainAxisField,
    comparisonMode,
    periodToDateData,
    isPeriodToDateError,
  });

  const reversedDeltaColors = direction === 'lower';
  const normalizedDelta =
    deltaValue !== undefined
      ? formatPropertyValue(deltaValue, {
          field,
          style: 'compactLong',
          format: comparisonType === 'percentage' ? 'percentage' : field.format,
        })
      : null;
  const formattedDelta = `${deltaValue !== undefined && deltaValue > 0 ? '+' : ''}${normalizedDelta}`;

  const isDeltaPositive =
    deltaValue !== undefined && (reversedDeltaColors ? deltaValue < 0 : deltaValue > 0);

  // Show loading for period-to-date mode if period-to-date query is loading
  const isLoading =
    loading || (comparisonMode === 'period_to_date' && periodToDateLoading === true);

  if (isLoading) {
    return (
      <div className={styles.bigNumberDeltaLoader}>
        <Skeleton delay={50} light />
      </div>
    );
  }

  if (deltaValue === undefined && deltaLabel === undefined) {
    return;
  }

  return (
    <div className={classNames(styles.delta, styles.bigNumberLabel)}>
      {deltaValue !== undefined && (
        <div
          className={classNames(styles.deltaValue, {
            [styles.positive]: deltaValue !== 0 && isDeltaPositive,
            [styles.negative]: deltaValue !== 0 && !isDeltaPositive,
          })}>
          {deltaValue !== 0 && (
            <Icon
              name="ArrowUp"
              size={14}
              className={classNames(styles.deltaArrow, {
                [styles.arrowFlipped]: deltaValue < 0,
              })}
            />
          )}
          {formattedDelta}
        </div>
      )}
      {deltaLabel !== undefined && <div>{deltaLabel}</div>}
    </div>
  );
};

export const BigNumber = ({
  field,
  comparisonField,
  comparisonType,
  direction,
  showLabel,
  data,
  loading = false,
  hasHeader,
  isTimeSeries,
  mainAxisField,
  comparisonMode,
  periodToDateData,
  isPeriodToDateLoading: periodToDateLoading,
  isPeriodToDateError,
}: BigNumberProps) => {
  return (
    <div
      className={classNames(styles.bigNumber, {
        [styles.hasHeader]: hasHeader,
        [styles.timeSeries]: isTimeSeries,
      })}>
      <BigNumberValue field={field} {...{ data, loading }} />
      <div className={styles.bigNumberLabel}>{field.name}</div>
      <BigNumberDelta
        {...{
          data,
          field,
          comparisonField,
          comparisonType,
          direction,
          showLabel,
          loading,
          mainAxisField,
          comparisonMode,
          periodToDateData,
          periodToDateLoading,
          isPeriodToDateError,
        }}
      />
    </div>
  );
};
